import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import BackTop from '@/components/base/BackTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SectionTitle from '@/components/base/SectionTitle';
import { characters } from '@/mocks/characters';
import { Link } from 'react-router-dom';

const pageSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: '师兄啊师兄人物关系图谱',
  url: 'https://pxwfrw.cn/characters',
  description: '师兄啊师兄核心人物一览与人物关系图谱，师兄师姐同门情谊细节整理。',
  inLanguage: 'zh-CN',
  mainEntity: {
    '@type': 'ItemList',
    itemListElement: characters.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      description: c.desc,
    })),
  },
};

export default function Characters() {
  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }} />
      <Header />
      <main className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '人物关系' }]} />
        <header className="mb-8 md:mb-12">
          <h1 className="font-heading text-3xl font-black tracking-wide text-foreground-950 md:text-4xl">
            师兄啊师兄 · 人物关系
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-foreground-600 md:text-base md:leading-8">
            从稳健的师兄李长寿，到温柔的小师妹蓝灵娥，再到又飒又沙雕的酒玖师叔——先把这群同门认全，
            才能读得懂人物关系的千丝万缕。点击下方栏目，继续探索 CP 线与师门日常。
          </p>
        </header>

        <section aria-label="人物卡片">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {characters.map((c) => (
              <article
                key={c.id}
                className="group flex flex-col overflow-hidden rounded-xl border border-background-200/70 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-primary-200"
              >
                <div className="relative h-64 w-full overflow-hidden md:h-72">
                  <img
                    src={c.image}
                    alt={c.name}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-md bg-background-950/60 px-2.5 py-1 text-xs text-background-50 backdrop-blur-sm">
                    {c.faction}
                  </span>
                  <span className="absolute right-3 top-3 rounded-md bg-primary-500/90 px-2.5 py-1 text-xs text-background-50">
                    {c.role}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h2 className="font-heading text-xl font-bold text-foreground-950">{c.name}</h2>
                      <p className="mt-0.5 text-xs text-foreground-500">{c.alias}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {c.tags.slice(0, 3).map((t) => (
                        <span key={t} className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                  <p className="mt-3 flex-1 text-sm leading-7 text-foreground-600">{c.desc}</p>
                  <blockquote className="mt-4 rounded-lg bg-background-100 px-4 py-3 text-sm italic leading-6 text-primary-700">
                    {c.highlight}
                  </blockquote>
                  <p className="mt-4 border-t border-background-200/70 pt-3 text-xs leading-6 text-foreground-500">
                    <span className="font-medium text-foreground-700">关系简述：</span>
                    {c.relationNote}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section aria-label="关系图谱" className="mt-16 md:mt-20">
          <SectionTitle
            mark="图谱"
            title="同门情谊 · 关系速览"
            subtitle="一份快速上手的角色关系图谱：谁与谁亦师亦友，谁是谁的牵挂，一条条线索帮你更快进入剧情。"
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {characters.map((c) => (
              <div
                key={c.id}
                className="flex items-start gap-3 rounded-xl border border-background-200/70 bg-background-100/60 p-4"
              >
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-primary-500 text-base font-bold text-background-50">
                  {c.name.slice(0, 1)}
                </span>
                <div>
                  <p className="font-heading text-sm font-bold text-foreground-950">{c.name}</p>
                  <p className="mt-1 text-xs leading-6 text-foreground-600">{c.relationNote}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section aria-label="继续探索" className="mt-16 rounded-xl bg-background-100/70 p-6 md:p-8">
          <div className="flex flex-col items-start justify-between gap-5 lg:flex-row lg:items-center">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">认得差不多了？</h2>
              <p className="mt-2 text-sm leading-6 text-foreground-600">接着去看 CP 线专题，或逛逛师门日常，把高甜名场面一网打尽。</p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link to="/cp" className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600">
                CP线专题
              </Link>
              <Link to="/daily" className="whitespace-nowrap rounded-md border border-primary-300 px-6 py-3 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-500 hover:text-background-50">
                师门日常
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackTop />
    </div>
  );
}